import { useEffect, useMemo, useState } from 'react'
import { subscribeAllReceipts, type CloudReceipt } from '../lib/cloudStore'
import type { ReceiptCategory } from '../lib/receipts'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

const CAT_COLOR: Record<ReceiptCategory, string> = {
  kundfakturor: '#10b981',
  leverantorsfakturor: '#ef4444',
  utlagg: '#3b82f6',
}

const CAT_LABEL: Record<ReceiptCategory, string> = {
  kundfakturor: 'Kund',
  leverantorsfakturor: 'Lev.',
  utlagg: 'Utlägg',
}

interface YearStats {
  year: number
  total: number
  totalAmount: number
  byCategory: Record<ReceiptCategory, number>
  byCategoryAmount: Record<ReceiptCategory, number>
  byMonth: { count: number; amount: number; byCat: Record<ReceiptCategory, number>; byCatAmount: Record<ReceiptCategory, number> }[]
  qualityGreen: number
  qualityRed: number
}

function buildYearStats(receipts: CloudReceipt[]): YearStats[] {
  const map = new Map<number, CloudReceipt[]>()
  for (const r of receipts) {
    const arr = map.get(r.year) ?? []
    arr.push(r)
    map.set(r.year, arr)
  }

  const stats: YearStats[] = []
  for (const [year, recs] of map) {
    const byMonth = Array.from({ length: 12 }, () => ({
      count: 0,
      amount: 0,
      byCat: { kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 } as Record<ReceiptCategory, number>,
      byCatAmount: { kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 } as Record<ReceiptCategory, number>,
    }))
    const byCategory = { kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 } as Record<ReceiptCategory, number>
    const byCategoryAmount = { kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 } as Record<ReceiptCategory, number>
    let qualityGreen = 0
    let qualityRed = 0
    let totalAmount = 0

    for (const r of recs) {
      const m = r.month >= 0 && r.month <= 11 ? r.month : 0
      const amt = r.amount ?? 0
      byMonth[m].count++
      byMonth[m].amount += amt
      byMonth[m].byCat[r.category]++
      byMonth[m].byCatAmount[r.category] += amt
      byCategory[r.category]++
      byCategoryAmount[r.category] += amt
      totalAmount += amt
      if (r.quality === 'green' || r.quality === 'verified') qualityGreen++
      if (r.quality === 'red') qualityRed++
    }

    stats.push({
      year,
      total: recs.length,
      totalAmount,
      byCategory,
      byCategoryAmount,
      byMonth,
      qualityGreen,
      qualityRed,
    })
  }

  stats.sort((a, b) => b.year - a.year)
  return stats
}

interface Props {
  companyId: string
  companyName: string
  selectedYear: number
  onSelectYear: (y: number) => void
}

export function ImportOverview({ companyId, companyName, selectedYear, onSelectYear }: Props) {
  const [receipts, setReceipts] = useState<CloudReceipt[]>([])

  useEffect(() => {
    try {
      return subscribeAllReceipts(companyId, setReceipts)
    } catch {
      return () => {}
    }
  }, [companyId])

  const yearStats = useMemo(() => buildYearStats(receipts), [receipts])

  if (yearStats.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '1rem', textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
        Inga kvitton eller fakturor importerade för {companyName} ännu.
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Importöversikt — {companyName}
      </div>
      <div className="grid" style={{ gap: '0.75rem' }}>
        {yearStats.map((ys) => (
          <YearCard
            key={ys.year}
            stats={ys}
            isSelected={ys.year === selectedYear}
            onSelect={() => onSelectYear(ys.year)}
          />
        ))}
      </div>
    </div>
  )
}

function YearCard({ stats, isSelected, onSelect }: { stats: YearStats; isSelected: boolean; onSelect: () => void }) {
  const maxCount = Math.max(1, ...stats.byMonth.map((m) => m.count))
  const maxAmount = Math.max(1, ...stats.byMonth.map((m) => m.amount))
  const barH = 48
  const amtBarH = 36

  return (
    <div
      className="card"
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        borderLeft: isSelected ? '4px solid var(--primary)' : '4px solid transparent',
        background: isSelected ? 'rgba(59,130,246,0.06)' : undefined,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>{stats.year}</span>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {stats.total} dokument · {fmt(stats.totalAmount)} kr
        </span>
      </div>

      {/* Category pills */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        {(['kundfakturor', 'leverantorsfakturor', 'utlagg'] as ReceiptCategory[]).map((cat) => (
          <span
            key={cat}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: stats.byCategory[cat] > 0 ? CAT_COLOR[cat] + '18' : 'var(--bg-card-alt)',
              color: stats.byCategory[cat] > 0 ? CAT_COLOR[cat] : 'var(--text-muted)',
              fontWeight: 600,
              opacity: stats.byCategory[cat] > 0 ? 1 : 0.5,
            }}
          >
            <span style={{ fontSize: '0.6rem' }}>●</span>
            {CAT_LABEL[cat]} {stats.byCategory[cat]} st · {fmt(stats.byCategoryAmount[cat])} kr
          </span>
        ))}
        {stats.qualityRed > 0 && (
          <span style={{
            fontSize: '0.7rem',
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(245,158,11,0.12)',
            color: '#f59e0b',
            fontWeight: 600,
          }}>
            {stats.qualityRed} saknar data
          </span>
        )}
      </div>

      {/* Bar chart — antal per månad */}
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Antal per månad
      </div>
      <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: barH, marginBottom: '0.4rem' }}>
        {stats.byMonth.map((m, i) => {
          const h = m.count > 0 ? Math.max(3, (m.count / maxCount) * barH) : 0
          // Stacked bar per category
          const kunH = m.byCat.kundfakturor > 0 ? Math.max(1, (m.byCat.kundfakturor / m.count) * h) : 0
          const levH = m.byCat.leverantorsfakturor > 0 ? Math.max(1, (m.byCat.leverantorsfakturor / m.count) * h) : 0
          const utlH = h - kunH - levH
          return (
            <div
              key={i}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: barH }}
              title={`${MONTHS[i]}: ${m.count} st (${fmt(m.amount)} kr)`}
            >
              {m.count > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                  {utlH > 0 && <div style={{ height: utlH, background: CAT_COLOR.utlagg, opacity: 0.8 }} />}
                  {levH > 0 && <div style={{ height: levH, background: CAT_COLOR.leverantorsfakturor, opacity: 0.8 }} />}
                  {kunH > 0 && <div style={{ height: kunH, background: CAT_COLOR.kundfakturor, opacity: 0.8 }} />}
                </div>
              )}
              {m.count === 0 && (
                <div style={{ height: 1, background: 'var(--border)', opacity: 0.4, borderRadius: 1 }} />
              )}
            </div>
          )
        })}
      </div>

      {/* Bar chart — belopp per månad */}
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Belopp per månad
      </div>
      <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: amtBarH, marginBottom: '0.3rem' }}>
        {stats.byMonth.map((m, i) => {
          const h = m.amount > 0 ? Math.max(3, (m.amount / maxAmount) * amtBarH) : 0
          const kunH = m.byCatAmount.kundfakturor > 0 ? Math.max(1, (m.byCatAmount.kundfakturor / m.amount) * h) : 0
          const levH = m.byCatAmount.leverantorsfakturor > 0 ? Math.max(1, (m.byCatAmount.leverantorsfakturor / m.amount) * h) : 0
          const utlH = h - kunH - levH
          return (
            <div
              key={i}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: amtBarH }}
              title={`${MONTHS[i]}: ${fmt(m.amount)} kr`}
            >
              {m.amount > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                  {utlH > 0 && <div style={{ height: Math.max(0, utlH), background: CAT_COLOR.utlagg, opacity: 0.5 }} />}
                  {levH > 0 && <div style={{ height: levH, background: CAT_COLOR.leverantorsfakturor, opacity: 0.5 }} />}
                  {kunH > 0 && <div style={{ height: kunH, background: CAT_COLOR.kundfakturor, opacity: 0.5 }} />}
                </div>
              )}
              {m.amount === 0 && (
                <div style={{ height: 1, background: 'var(--border)', opacity: 0.3, borderRadius: 1 }} />
              )}
            </div>
          )
        })}
      </div>

      {/* Month labels */}
      <div style={{ display: 'flex', gap: 2 }}>
        {MONTHS.map((m) => (
          <span key={m} style={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: 'var(--text-muted)', opacity: 0.6 }}>{m}</span>
        ))}
      </div>
    </div>
  )
}
